import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Tag, Button, Table, Transfer,
  message, Tabs, Descriptions, Space, Spin, Select, Form, Input, Switch,
} from 'antd';
import { ArrowLeft, Save } from 'lucide-react';
import { usersApi, propertiesApi, leadsApi, particularsApi } from '../../services/api';

const { Title, Text } = Typography;

const MODULE_OPTIONS = [
  { label: 'Leads', value: 'leads' },
  { label: 'Propiedades', value: 'properties' },
  { label: 'Oportunidades', value: 'opportunities' },
  { label: 'Reuniones', value: 'meetings' },
  { label: 'Blog', value: 'blog' },
  { label: 'Assets', value: 'assets' },
  { label: 'Integraciones', value: 'integrations' },
  { label: 'Valorador AVM', value: 'avm' },
  { label: 'Listado Particulares', value: 'listado-particulares' },
  { label: 'Inmovilla', value: 'inmovilla' },
  { label: 'Idealista', value: 'idealista' },
];

function parsePermissions(permissions: string): string[] {
  if (!permissions || permissions === 'all') return MODULE_OPTIONS.map(m => m.value);
  try { return JSON.parse(permissions); } catch { return []; }
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [allParticulares, setAllParticulares] = useState<any[]>([]);
  const [selectedParticulares, setSelectedParticulares] = useState<string[]>([]);
  const [editForm] = Form.useForm();

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [userRes, propsRes, leadsRes, allParticularsRes] = await Promise.all([
        usersApi.get(id),
        propertiesApi.list({ limit: 200 }),
        leadsApi.list({ limit: 200 }),
        particularsApi.list({ limit: 500 }).catch(() => ({ data: { data: [] } })),
      ]);
      const u = userRes.data;
      setUser(u);
      setSelectedModules(parsePermissions(u.permissions));
      setSelectedProperties((u.properties || []).map((p: any) => p.id));
      setSelectedLeads((u.leads || []).map((l: any) => l.id));
      setAllProperties(propsRes.data.data || []);
      setAllLeads(leadsRes.data.data || []);
      const allParts = allParticularsRes.data.data || [];
      setAllParticulares(allParts);
      setSelectedParticulares(allParts.filter((p: any) => p.assignedToId === id).map((p: any) => p.id));
      editForm.setFieldsValue({ name: u.name, email: u.email, phone: u.phone });
    } catch {
      message.error('Error al cargar usuario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const savePermissions = async () => {
    setSaving(true);
    try {
      await usersApi.updatePermissions(id!, selectedModules);
      message.success('Permisos actualizados');
    } catch {
      message.error('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const saveProperties = async () => {
    setSaving(true);
    try {
      await usersApi.assignProperties(id!, selectedProperties);
      message.success('Propiedades asignadas');
      load();
    } catch {
      message.error('Error al asignar propiedades');
    } finally {
      setSaving(false);
    }
  };

  const saveLeads = async () => {
    setSaving(true);
    try {
      await usersApi.assignLeads(id!, selectedLeads);
      message.success('Leads asignados');
    } catch {
      message.error('Error al asignar leads');
    } finally {
      setSaving(false);
    }
  };

  const saveParticulares = async () => {
    setSaving(true);
    try {
      // Assign newly added ones to this user; remove ones that were deselected
      const prevAssigned = allParticulares.filter((p: any) => p.assignedToId === id).map((p: any) => p.id);
      const toAssign = selectedParticulares.filter((pid) => !prevAssigned.includes(pid));
      const toUnassign = prevAssigned.filter((pid) => !selectedParticulares.includes(pid));
      await Promise.all([
        ...toAssign.map((pid) => particularsApi.assign(pid, id!)),
        ...toUnassign.map((pid) => particularsApi.assign(pid, null)),
      ]);
      message.success('Particulares actualizados');
      load();
    } catch {
      message.error('Error al asignar particulares');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async (values: any) => {
    setSaving(true);
    try {
      await usersApi.update(id!, values);
      message.success('Perfil actualizado');
      load();
    } catch {
      message.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  if (!user) return null;

  const isAdmin = user.role === 'ADMIN';

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 24, gap: 12 }}>
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/users')}>
          Usuarios
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {user.name}{' '}
          <Tag color={user.role === 'ADMIN' ? 'gold' : 'blue'}>{user.role}</Tag>
          <Tag color={user.isActive ? 'green' : 'red'}>{user.isActive ? 'Activo' : 'Inactivo'}</Tag>
        </Title>
      </Row>

      <Tabs
        items={[
          {
            key: 'info',
            label: 'Perfil',
            children: (
              <Card>
                <Form form={editForm} layout="vertical" onFinish={saveProfile}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="phone" label="Teléfono">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="password" label="Nueva contraseña (dejar vacío para no cambiar)">
                        <Input.Password />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="isActive" label="Activo" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button type="primary" htmlType="submit" icon={<Save size={16} />} loading={saving}>
                    Guardar cambios
                  </Button>
                </Form>
              </Card>
            ),
          },
          ...(!isAdmin ? [
            {
              key: 'permissions',
              label: 'Módulos',
              children: (
                <Card
                  title="Módulos habilitados"
                  extra={
                    <Button type="primary" size="small" loading={saving} onClick={savePermissions}>
                      Guardar
                    </Button>
                  }
                >
                  <Select
                    mode="multiple"
                    value={selectedModules}
                    onChange={setSelectedModules}
                    options={MODULE_OPTIONS}
                    style={{ width: '100%' }}
                    placeholder="Selecciona los módulos accesibles para este agente"
                  />
                  <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    El agente solo verá en el menú lateral los módulos que tengas habilitados aquí.
                  </Text>
                </Card>
              ),
            },
            {
              key: 'properties',
              label: `Propiedades (${selectedProperties.length})`,
              children: (
                <Card
                  title="Propiedades asignadas a este agente"
                  extra={
                    <Button type="primary" size="small" loading={saving} onClick={saveProperties}>
                      Guardar asignación
                    </Button>
                  }
                >
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                    Al guardar, <strong>reemplaza</strong> la lista completa de propiedades asignadas.
                    Las propiedades no seleccionadas quedarán sin agente.
                  </Text>
                  <Transfer
                    dataSource={allProperties.map(p => ({
                      key: p.id,
                      title: `${p.title} — ${p.city || ''}`,
                      description: p.reference || '',
                    }))}
                    targetKeys={selectedProperties}
                    onChange={(keys) => setSelectedProperties(keys as string[])}
                    render={item => item.title}
                    showSearch
                    listStyle={{ width: '100%', height: 350 }}
                    titles={['Disponibles', 'Asignadas']}
                    locale={{ itemUnit: 'prop', itemsUnit: 'props', searchPlaceholder: 'Buscar...' }}
                  />
                </Card>
              ),
            },
            {
              key: 'leads',
              label: `Leads (${selectedLeads.length})`,
              children: (
                <Card
                  title="Leads asignados"
                  extra={
                    <Button type="primary" size="small" loading={saving} onClick={saveLeads}>
                      Asignar seleccionados
                    </Button>
                  }
                >
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                    Asigna leads adicionales a este agente. Los leads ya asignados a otros agentes no se reasignan automáticamente.
                  </Text>
                  <Transfer
                    dataSource={allLeads.map(l => ({
                      key: l.id,
                      title: `${l.firstName} ${l.lastName || ''} — ${l.status}`,
                    }))}
                    targetKeys={selectedLeads}
                    onChange={(keys) => setSelectedLeads(keys as string[])}
                    render={item => item.title}
                    showSearch
                    listStyle={{ width: '100%', height: 350 }}
                    titles={['Sin asignar / Otros', 'Asignados aquí']}
                    locale={{ itemUnit: 'lead', itemsUnit: 'leads', searchPlaceholder: 'Buscar...' }}
                  />
                </Card>
              ),
            },
            {
              key: 'particulares',
              label: `Listado Particulares (${selectedParticulares.length})`,
              children: (
                <Card
                  title="Particulares de Idealista asignados a este agente"
                  extra={
                    <Button type="primary" size="small" loading={saving} onClick={saveParticulares}>
                      Guardar asignación
                    </Button>
                  }
                >
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                    Asigna registros de particulares a este agente. Los que estén asignados a otro agente se reasignarán.
                  </Text>
                  <Transfer
                    dataSource={allParticulares.map((p: any) => ({
                      key: p.id,
                      title: [p.contactName, p.contactPhone, p.address || p.municipality].filter(Boolean).join(' — '),
                      description: p.price ? `${p.price.toLocaleString('es-ES')} €` : '',
                    }))}
                    targetKeys={selectedParticulares}
                    onChange={(keys) => setSelectedParticulares(keys as string[])}
                    render={(item) => item.title}
                    showSearch
                    listStyle={{ width: '100%', height: 380 }}
                    titles={['Sin asignar / Otros', 'Asignados aquí']}
                    locale={{ itemUnit: 'particular', itemsUnit: 'particulares', searchPlaceholder: 'Buscar...' }}
                  />
                </Card>
              ),
            },
          ] : []),
        ]}
      />
    </div>
  );
}
